/**
 * Posts context — provides post data + action functions.
 *
 * New components use: const { posts, approve, swap } = usePosts();
 * Existing NectarApp.jsx continues using its own useState until migrated.
 *
 * Usage:
 *   import { usePosts } from '../context/PostsContext';
 *   const { thisWeek, pastPosts, loading, approve, doSwap, rewrite } = usePosts();
 */

import { createContext, useContext, useState, useCallback } from 'react';
import api from '../services/api';

const PostsContext = createContext(null);

export function PostsProvider({ children }) {
  const [thisWeek, setThisWeek] = useState([]);
  const [pastPosts, setPastPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rewriteUsage, setRewriteUsage] = useState({ remaining: null, resets_at: null });

  // Swap state
  const [swapPanelPostId, setSwapPanelPostId] = useState(null);
  const [swapAlternatives, setSwapAlternatives] = useState([]);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swappingPostId, setSwappingPostId] = useState(null);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/app/api/user/scheduled-posts');
      if (data) {
        if (data.posts) {
          setThisWeek(data.posts);
        } else if (Array.isArray(data)) {
          setThisWeek(data);
        }
      }
    } catch (e) {
      setError(e.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPastPosts = useCallback(async () => {
    try {
      const data = await api.get('/app/api/user/post-history');
      if (data && Array.isArray(data)) setPastPosts(data);
      else if (data?.posts) setPastPosts(data.posts);
    } catch { /* silent */ }
  }, []);

  const approve = useCallback(async (postId) => {
    try {
      await api.patch(`/app/api/user/scheduled-posts/${postId}`, { status: 'approved' });
      setThisWeek(prev => prev.map(p => p.id === postId ? { ...p, status: 'approved' } : p));
      return true;
    } catch { return false; }
  }, []);

  const deletePost = useCallback(async (postId) => {
    try {
      await api.delete(`/app/api/user/scheduled-posts/${postId}`);
      setThisWeek(prev => prev.filter(p => p.id !== postId));
      return true;
    } catch { return false; }
  }, []);

  const openSwapPanel = useCallback(async (postId) => {
    setSwapPanelPostId(postId);
    setSwapLoading(true);
    setSwapAlternatives([]);
    try {
      const data = await api.get(`/app/api/user/scheduled-posts/${postId}/alternative-articles?limit=3`);
      setSwapAlternatives(Array.isArray(data) ? data : []);
    } catch {
      setSwapAlternatives([]);
    } finally {
      setSwapLoading(false);
    }
  }, []);

  const doSwap = useCallback(async (postId, articleUrl) => {
    setSwappingPostId(postId);
    try {
      const data = await api.patch(`/app/api/user/scheduled-posts/${postId}/swap-article`, { article_url: articleUrl });
      if (data?.ok) {
        if (typeof data.rewrites_remaining === 'number') {
          setRewriteUsage(u => ({ ...u, remaining: data.rewrites_remaining }));
        }
        await loadPosts();
        return data;
      }
      return null;
    } catch { return null; }
    finally { setSwappingPostId(null); }
  }, [loadPosts]);

  const closeSwapPanel = useCallback(() => {
    setSwapPanelPostId(null);
    setSwapAlternatives([]);
  }, []);

  const pendingCount = thisWeek.filter(p => p.status === 'pending').length;

  return (
    <PostsContext.Provider value={{
      thisWeek, setThisWeek, pastPosts, setPastPosts,
      loading, error, loadPosts, loadPastPosts,
      rewriteUsage, setRewriteUsage, pendingCount,
      approve, deletePost,
      swapPanelPostId, swapAlternatives, swapLoading, swappingPostId,
      openSwapPanel, doSwap, closeSwapPanel,
    }}>
      {children}
    </PostsContext.Provider>
  );
}

export function usePosts() {
  const ctx = useContext(PostsContext);
  if (!ctx) throw new Error('usePosts must be used within PostsProvider');
  return ctx;
}

export default PostsContext;
